import { useDrugDetail } from '@/entities/drug/hooks/useDrugQueries';
import { shortDrugName } from '@/entities/drug/utils/drug.utils';
import { DrugDetailView } from '@/shared/components/drug-drawer/DrugDetailView';
import { useDrugDrawer } from '@/shared/components/drug-drawer/useDrugDrawer';
import { Breadcrumbs } from '@/shared/components/ui/breadcrumbs';
import { Button } from '@/shared/components/ui/button';
import { Icon } from '@/shared/components/ui/icon';
import {
  Sheet,
  SheetContent,
  SheetDescription,
} from '@/shared/components/ui/sheet';
import { cn } from '@/shared/lib/utils';
import { formatNumber } from '@/shared/utils/format';
import { useUIStore } from '@/stores/uiStore';
import { useSearch } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';

/** 브레드크럼 한 칸 — 드로어 안에서 거쳐 온 약 */
interface Crumb {
  drugCode: string;
  drugName: string;
}

/**
 * M-12 의약품 상세 드로어 (Figma 833:6613) — 앱 레이아웃에 한 번만 둔다.
 * 열림 상태는 URL ?drug=보험코드 하나로 관리한다 (M-15 D). 여는 쪽은 useDrugDrawer().open(drugCode)를 쓴다.
 * v1.0 디자인은 별도 헤더 줄 없이 약명 줄 오른쪽에 ✕만 둔다. [링크 복사]는 없앴다 (기획↔디자인 변경 912:6416).
 *
 * 드로어 안에서 동일성분 약을 누르면 새로 쌓지 않고 내용만 바꾼다(M-15 B 교체).
 * 거쳐 온 약은 브레드크럼 스택으로 들고 있고, 브레드크럼으로 돌아간다.
 */
export const DrugDrawerHost = () => {
  const { drug } = useSearch({ strict: false }) as { drug?: string };
  const { close, replace } = useDrugDrawer();
  // 거래 정보 드로어 위에 2단으로 쌓일 때 24px 왼쪽으로 비켜 연다 (M-15 B). 모바일은 전체 폭 교체
  const stacked = useUIStore((s) => s.drugDrawerStacked);
  const drugContext = useUIStore((s) => s.drugContext);
  const setDrugContext = useUIStore((s) => s.setDrugContext);
  // 드로어 안에서 다른 약으로 바뀌면 처방량 문맥은 보여 주지 않는다
  const context = drugContext?.drugCode === drug ? drugContext : null;
  const [trail, setTrail] = useState<Crumb[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);

  // 닫히면 브레드크럼을 비운다
  useEffect(() => {
    if (!drug) {
      setTrail([]);
      setDrugContext(null);
    }
  }, [drug, setDrugContext]);

  // 약이 바뀌면 본문을 맨 위로 올린다
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [drug]);

  const selectRelated = (current: Crumb, nextCode: string) => {
    setTrail((t) => [...t, current]);
    void replace(nextCode);
  };

  const goToCrumb = (index: number) => {
    const target = trail[index];
    if (!target) return;
    setTrail((t) => t.slice(0, index));
    void replace(target.drugCode);
  };

  const closeButton = (
    <Button
      variant="text"
      size="medium"
      aria-label="닫기"
      onClick={close}
      leftIcon={<Icon name="cancel-line-24" />}
    />
  );

  return (
    <Sheet open={!!drug} onOpenChange={(open) => !open && close()}>
      <SheetContent
        side="right"
        overlay="drawer"
        className={cn('bg-surface-grey', stacked && 'lg:right-6')}
      >
        <SheetDescription className="sr-only">
          보험코드 {drug} 의약품 상세
        </SheetDescription>

        <div ref={scrollRef} className="flex-1 overflow-y-auto">
          {/* 거쳐 온 약 (M-15 B 교체) — Figma에 없는 상태라 Breadcrumbs 컴포넌트로 흰 영역 맨 위에 둔다 */}
          {trail.length > 0 && (
            <div className="bg-surface-white px-5 pt-6">
              <Breadcrumbs
                aria-label="거쳐 온 의약품"
                items={[
                  // href에는 스택 위치를 담아 renderLink에서 돌아갈 칸을 찾는다
                  ...trail.map((c, i) => ({
                    label: shortDrugName(c.drugName),
                    href: `#${i}`,
                  })),
                  { label: <CurrentCrumb drugCode={drug} /> },
                ]}
                renderLink={(item, children) => (
                  <button
                    type="button"
                    onClick={() => goToCrumb(Number(item.href?.slice(1)))}
                  >
                    {children}
                  </button>
                )}
              />
            </div>
          )}

          {/* M-15 C — 실적에서 열었을 때 그 달 그 병의원의 처방량 */}
          {context && (
            <div className="bg-surface-white px-6 pt-6">
              <p className="rounded-12 bg-brand-75 px-4 py-3 text-body-14-m text-fg-secondary">
                {context.label} 처방량{' '}
                <span className="text-body-14-sb text-fg-accent">
                  {formatNumber(context.quantity)}
                </span>
              </p>
            </div>
          )}

          {drug && (
            <DrugDetailView
              key={drug}
              drugCode={drug}
              closeButton={closeButton}
              compactTop={trail.length > 0 || !!context}
              onSelectRelated={selectRelated}
            />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

/** 현재 약 이름 — 상세 캐시에서 읽는다 (DrugDetailView와 같은 쿼리) */
const CurrentCrumb = ({ drugCode }: { drugCode?: string }) => {
  const { data } = useDrugDetail(drugCode);
  return <>{data ? shortDrugName(data.drug.drugName) : drugCode}</>;
};
